<template>
  <div class="map-box">
    <div id="deviceBMap"></div>
    <div class="map-theme" v-if="hasThemeSwitcher">
      <span
        v-for="v in mapThemes"
        :key="v.id"
        :title="v.label"
        :style="{ backgroundColor: v.color }"
        @click="changeMapTheme(v.id)"
        :class="{ 'is-active': v.id === mapThemeId }"
      ></span>
    </div>
    <div class="modify-position" v-show="sn">
      <el-row type="flex" style="margin-bottom: 4px" v-if="hasSearch">
        <el-input
          v-model="area"
          size="mini"
          style="margin-right: 8px"
          @keyup.native.enter="search"
        ></el-input>
        <el-button type="primary" size="mini" @click="search">
          {{ '搜索' | translateTo(i18nPath) }}
        </el-button>
        <el-button type="primary" size="mini" icon="el-icon-position" @click="positing"></el-button>
      </el-row>
      <el-form v-if="revisability" inline size="mini" :model="deviceInfo" label-suffix=":">
        <el-form-item :label="$t('名称')">
          <el-input v-model="deviceInfo.name"></el-input>
        </el-form-item>
        <el-form-item :label="$t('设备位置')">
          <el-input v-model="deviceInfo.locationDesc"></el-input>
        </el-form-item>
        <el-form-item :label="$t('经度')">
          <el-input v-model="deviceInfo.longitude"></el-input>
        </el-form-item>
        <el-form-item :label="$t('纬度')">
          <el-input v-model="deviceInfo.latitude"></el-input>
        </el-form-item>
        <el-form-item class="ignore">
          <el-button type="primary" size="mini" @click="updateInfo">
            {{ $t('信息修改') }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import { mapThemes } from '@/utils/dict';
import { isLocal, isSupportCanvas } from '@/utils/utils';
import Type from '@/store/mutation-type';
import { loadBMapHeat, loadBMapV3 } from '@/utils/map';
import bmapTheme from '@/utils/bmapTheme';
import { updateDeviceInfo } from '@/api/devices';
import { isEmpty } from '@/utils/is';

export default {
  name: 'BMap',
  props: {
    hasHeatmap: {
      type: Boolean,
      default: false
    },
    initPoint: {
      type: Object
    },
    revisability: {
      type: Boolean,
      default: true
    },
    sn: {
      type: String,
      default: ''
    },
    hasSearch: {
      type: Boolean,
      default: true
    },
    hasThemeSwitcher: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      i18nPath: 'component.bmap',
      bmap: null,
      mapThemes,
      markers: {},
      area: '',
      infoWindow: null,
      colors: {
        '-1': 'unconnect-color',
        0: 'success-color',
        1: 'warning-color',
        2: 'danger-color',
        61: 'fix-color',
        71: 'fault-color',
        81: 'collect-color'
      },
      deviceInfo: {
        sn: '',
        name: '',
        longitude: '',
        latitude: '',
        locationDesc: ''
      }
    };
  },
  computed: {
    ...mapState('map', ['mapThemeId', 'mapZoom']),
    ...mapState('admin', ['profile']),
    ...mapState('socket', ['devicesSourseMap', 'deviceSNMap']),
    filterNoPosDevices() {
      return this.devicesSourseMap.filter(e => e.latitude !== 0 || e.longitude !== 0);
    },
    localSearch() {
      // eslint-disable-next-line no-undef
      return new BMap.LocalSearch(this.bmap, {
        renderOptions: { map: this.bmap }
      });
    },
    currentChooseDevice() {
      return this.deviceSNMap[this.sn || this.$route.params.sn];
    },
    isOfflineMap() {
      return isLocal() || !isEmpty(this.profile?.mapTile);
    }
  },
  methods: {
    ...mapMutations('socket', {
      modifyDeviceNameMuta: Type.MODIFY_DEVICE_INFO
    }),
    ...mapMutations('map', {
      changeMapTheme: Type.CHANGE_MAP_THEME,
      chengeMapZoom: Type.MAP_ZOOM
    }),
    search() {
      if (this.profile.mapTile) {
        this.$message.warning('离线不支持搜索功能');
        return;
      }
      const { area } = this;
      this.bmap.centerAndZoom(area, this.mapZoom);
      this.localSearch.search(this.area);
    },
    updateInfo() {
      this.$confirm('确定修改设备相关信息吗', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        updateDeviceInfo(this.deviceInfo).then(() => {
          this.$message({
            type: 'success',
            message: '修改成功',
            showClose: true
          });
          this.modifyDeviceNameMuta(this.deviceInfo);
        });
      });
    },
    positing() {
      const initPoint = new BMap.Point(116.403748, 39.915055);
      this.bmap.centerAndZoom(this.initPoint || initPoint, this.mapZoom);
    },
    centerAndZoom(point, zoom) {
      this.bmap.centerAndZoom(point, zoom);
    },
    setMapStyleV2(theme) {
      this.bmap.setMapStyleV2(theme);
    },
    addOverlay(marker) {
      this.bmap.addOverlay(marker);
    },
    clearOverlays() {
      this.bmap.clearOverlays();
    },
    addControl(control) {
      this.bmap.addControl(control);
    },
    openInfoWindow(infoWindow, point) {
      this.bmap.openInfoWindow(infoWindow, point);
    },
    setMapType(type) {
      this.bmap.setMapType(type);
    },
    setMinZoom(zoom) {
      this.bmap.setMinZoom(zoom);
    },
    setMaxZoom(zoom) {
      this.bmap.setMaxZoom(zoom);
    },
    initBasicData() {
      this.deviceInfo.sn = this.sn || this.$route.params.sn;

      if (!isEmpty(this.currentChooseDevice)) {
        this.deviceInfo.name = this.currentChooseDevice.name;
        this.deviceInfo.longitude = this.currentChooseDevice.longitude;
        this.deviceInfo.latitude = this.currentChooseDevice.latitude;
        this.deviceInfo.locationDesc =
          this.currentChooseDevice?.dynamicInfoMap?.location_desc || '';
      }
    }
  },
  async mounted() {
    this.initBasicData();

    if (!isSupportCanvas) {
      this.$message({ type: 'warning', message: '浏览器不支持canvas无法展示模型，请更换浏览器' });
    } else {
      await loadBMapV3();
      if (this.hasHeatmap) await loadBMapHeat();
      // eslint-disable-next-line no-undef
      this.bmap = new BMap.Map('deviceBMap');
      this.bmap.enableScrollWheelZoom();
      this.bmap.enableContinuousZoom();

      this.bmap.addEventListener('rightclick', ({ point }) => {
        this.deviceInfo.longitude = point.lng;
        this.deviceInfo.latitude = point.lat;
      });

      this.bmap.addEventListener('zoomend', type => {
        this.chengeMapZoom(type.target.getZoom());
      });

      if (this.isOfflineMap) {
        this.bmap.setMaxZoom(13);
        this.bmap.setMinZoom(3);
      }

      this.$emit('loaded', this);
      this.$emit('mapHandler', this.isOfflineMap ? '' : bmapTheme[this.mapThemeId]);
    }
  },
  watch: {
    mapThemeId(newVal) {
      if (!this.profile.mapTile && this.bmap) {
        this.bmap.setMapStyleV2({ styleJson: bmapTheme[newVal] });
      }
    }
  }
};
</script>

<style lang="scss">
.map-box {
  height: 100%;
  position: relative;
  #deviceBMap {
    height: 100%;
  }
  .map-theme {
    display: flex;
    position: absolute;
    right: 10px;
    bottom: 10px;
    span {
      display: inline-block;
      width: 20px;
      height: 20px;
      margin: 0 3px;
      border: 1px solid #393646;
      &.is-active {
        border-color: #883535;
      }
    }
  }
  .BMapLabel {
    font-size: 14px;
    border: 1px solid #f5f5f5;
    background: #fff;
    color: #409eff;
  }
  .custom-info-content {
    color: #aaa;
    margin-top: 10px;

    .info-wrap {
      display: flex;
    }

    .left {
      img {
        width: 100px;
      }
    }

    .right > div {
      line-height: 26px;
    }
    .el-button {
      float: right;
    }
    .state-text {
      @include state-textstyle;
    }
  }
  .modify-position {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: rgba(18, 30, 44, 0.6);
    padding: 5px;

    min-width: 800px;
    width: 80%;
    max-width: 1000px;

    .el-form {
      display: flex;
      .el-form-item {
        display: flex;
        margin-bottom: 0;
        margin-right: 5px;
        .el-form-item__label {
          white-space: nowrap;
          // min-width: 52px;
          // white-space: nowrap;
          // width: -webkit-fill-available;
        }
      }
      .ignore {
        margin: 0;
      }
    }
  }
}
</style>
